import { useState, useEffect, useCallback } from 'react';
import { authedFetch, useAuth } from '@/lib/auth';
import { formatJst } from '@/lib/utils';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Power, PauseCircle, PlayCircle, Clock, Loader2, ShieldAlert } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const CTRL = `${API}/api/admin/live-customers/runtime-controls`;

/**
 * OperatorControls — global PAUSE/KILL-ALL switch + editable daily report time.
 * Host-only. When paused, NO live MEISAI run (daily/weekly/monthly/backfill/
 * manual/customer-initiated) processes or sends — accounts stay pending.
 */
export const OperatorControls = ({ onChange }) => {
  const { user } = useAuth();
  const isHost = user?.role === 'host';
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hour, setHour] = useState('7');
  const [minute, setMinute] = useState('0');
  const [savingTime, setSavingTime] = useState(false);
  const [wDay, setWDay] = useState('mon');
  const [wHour, setWHour] = useState('8');
  const [wMinute, setWMinute] = useState('30');
  const [savingWeekly, setSavingWeekly] = useState(false);
  const [mDay, setMDay] = useState('1');
  const [mHour, setMHour] = useState('10');
  const [mMinute, setMMinute] = useState('0');
  const [savingMonthly, setSavingMonthly] = useState(false);

  const load = useCallback(async () => {
    if (!isHost) return; // runtime-controls is host-only; skip for non-host admins
    try {
      const res = await authedFetch(`${CTRL}`, { credentials: 'include' });
      const d = await res.json();
      if (d.ok) {
        setC(d);
        setHour(String(d.daily_hour));
        setMinute(String(d.daily_minute));
        if (d.weekly_day) setWDay(String(d.weekly_day));
        if (d.weekly_hour != null) setWHour(String(d.weekly_hour));
        if (d.weekly_minute != null) setWMinute(String(d.weekly_minute));
        if (d.monthly_day != null) setMDay(String(d.monthly_day));
        if (d.monthly_hour != null) setMHour(String(d.monthly_hour));
        if (d.monthly_minute != null) setMMinute(String(d.monthly_minute));
        onChange?.(d);
      }
    } catch { /* noop */ }
  }, [onChange, isHost]);

  useEffect(() => { load(); }, [load]);

  const togglePause = async () => {
    const next = !c?.paused;
    if (next && !window.confirm('PAUSE all MEISAI processing? No daily/weekly/monthly/backfill or manual reports will be pulled or sent until you resume. Accounts stay pending — nothing is lost.')) return;
    setBusy(true);
    try {
      let reason = '';
      if (next) reason = window.prompt('Optional note (why are you pausing?)', '') || '';
      const res = await authedFetch(`${CTRL}/pause`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paused: next, reason }),
      });
      const d = await res.json();
      if (d.ok) {
        setC(d); onChange?.(d);
        toast[next ? 'warning' : 'success'](next ? 'System PAUSED — nothing will go to MEISAI.' : 'System RESUMED — normal processing restored.');
      } else { toast.error(d.error || 'Failed'); }
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const saveTime = async () => {
    const h = parseInt(hour, 10); const m = parseInt(minute, 10);
    if (Number.isNaN(h) || h < 0 || h > 23 || Number.isNaN(m) || m < 0 || m > 59) {
      toast.error('Enter hour 0–23 and minute 0–59'); return;
    }
    setSavingTime(true);
    try {
      const res = await authedFetch(`${CTRL}/daily-time`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hour: h, minute: m }),
      });
      const d = await res.json();
      if (d.ok) {
        setC(d); onChange?.(d);
        toast.success(`Daily report time set to ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} JST${d.rescheduled_live ? ' (live)' : ''}`);
      } else { toast.error(d.error || 'Failed'); }
    } catch (e) { toast.error(e.message); }
    setSavingTime(false);
  };

  const saveWeekly = async () => {
    const h = parseInt(wHour, 10); const m = parseInt(wMinute, 10);
    if (Number.isNaN(h) || h < 0 || h > 23 || Number.isNaN(m) || m < 0 || m > 59) {
      toast.error('Enter hour 0–23 and minute 0–59'); return;
    }
    setSavingWeekly(true);
    try {
      const res = await authedFetch(`${CTRL}/weekly-time`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ day: wDay, hour: h, minute: m }),
      });
      const d = await res.json();
      if (d.ok) {
        setC(d); onChange?.(d);
        toast.success(`Weekly report set to ${wDay.toUpperCase()} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} JST${d.rescheduled_live ? ' (live)' : ''}`);
      } else { toast.error(d.error || 'Failed'); }
    } catch (e) { toast.error(e.message); }
    setSavingWeekly(false);
  };

  const saveMonthly = async () => {
    const dy = parseInt(mDay, 10);
    const h = parseInt(mHour, 10); const m = parseInt(mMinute, 10);
    if (Number.isNaN(dy) || dy < 1 || dy > 28) {
      toast.error('Enter day of month 1–28'); return;
    }
    if (Number.isNaN(h) || h < 0 || h > 23 || Number.isNaN(m) || m < 0 || m > 59) {
      toast.error('Enter hour 0–23 and minute 0–59'); return;
    }
    setSavingMonthly(true);
    try {
      const res = await authedFetch(`${CTRL}/monthly-time`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ day: dy, hour: h, minute: m }),
      });
      const d = await res.json();
      if (d.ok) {
        setC(d); onChange?.(d);
        toast.success(`Monthly report set to day ${dy} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} JST${d.rescheduled_live ? ' (live)' : ''}`);
      } else { toast.error(d.error || 'Failed'); }
    } catch (e) { toast.error(e.message); }
    setSavingMonthly(false);
  };

  const DOM = Array.from({ length: 28 }, (_, i) => i + 1);

  const DOW = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];

  if (!c) return null;
  const paused = !!c.paused;

  // Live "today vs tomorrow" prediction so the operator knows, before saving,
  // whether the chosen time still fires today or rolls to tomorrow. A daily
  // cron fires at the NEXT occurrence of the clock time — so a time already
  // past in JST today means the next automatic run is tomorrow.
  const jstNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
  const chH = parseInt(hour, 10);
  const chM = parseInt(minute, 10);
  const timeValid = !Number.isNaN(chH) && chH >= 0 && chH <= 23 && !Number.isNaN(chM) && chM >= 0 && chM <= 59;
  const runsToday = timeValid && (chH > jstNow.getHours() || (chH === jstNow.getHours() && chM > jstNow.getMinutes()));
  const chLabel = timeValid ? `${String(chH).padStart(2, '0')}:${String(chM).padStart(2, '0')}` : '';

  return (
    <Card data-testid="operator-controls" className={paused ? 'border-2 border-rose-500' : 'border-2 border-slate-900'}>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-slate-900" />
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-900">Operator Controls</h3>
        </div>

        {/* PAUSE / KILL-ALL */}
        <div className={`flex items-center justify-between gap-3 rounded-lg p-3 ${paused ? 'bg-rose-50' : 'bg-slate-50'}`}>
          <div>
            <p className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Power className={`w-4 h-4 ${paused ? 'text-rose-600' : 'text-emerald-600'}`} />
              {paused ? 'PAUSED — nothing goes to MEISAI' : 'LIVE — processing normally'}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {paused
                ? `Paused${c.paused_by ? ` by ${c.paused_by}` : ''}${c.pause_reason ? ` · ${c.pause_reason}` : ''}. Reports stay pending until resumed.`
                : 'Halts all daily / weekly / monthly / 6-month backfill / manual sends instantly.'}
            </p>
          </div>
          <button
            type="button" onClick={togglePause} disabled={busy}
            data-testid="operator-pause-toggle"
            className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50 ${paused ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : paused ? <PlayCircle className="w-4 h-4" /> : <PauseCircle className="w-4 h-4" />}
            {paused ? 'Resume' : 'Pause all'}
          </button>
        </div>

        {/* Daily time editor */}
        <div className="rounded-lg bg-slate-50 p-3" data-testid="operator-daily-editor">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> Daily report time (JST)
            <span
              data-testid="operator-daily-status"
              className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${(c.scheduler_enabled && !c.paused) ? 'bg-emerald-100 text-emerald-700' : c.paused ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'}`}
            >
              {!c.scheduler_enabled ? 'AUTOMATION OFF' : c.paused ? 'PAUSED' : 'AUTOMATION ON'}
            </span>
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="number" min="0" max="23" value={hour} onChange={(e) => setHour(e.target.value)}
              data-testid="operator-daily-hour"
              className="w-16 min-h-[44px] px-2 py-1.5 border border-slate-300 rounded-lg text-sm font-mono text-center"
            />
            <span className="font-bold text-slate-400">:</span>
            <input
              type="number" min="0" max="59" value={minute} onChange={(e) => setMinute(e.target.value)}
              data-testid="operator-daily-minute"
              className="w-16 min-h-[44px] px-2 py-1.5 border border-slate-300 rounded-lg text-sm font-mono text-center"
            />
            <span className="text-[11px] text-slate-500">JST</span>
            <button
              type="button" onClick={saveTime} disabled={savingTime}
              data-testid="operator-daily-time-save"
              className="ml-auto inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 py-1.5 rounded-lg text-sm font-bold text-white bg-slate-900 hover:bg-slate-700 disabled:opacity-50"
            >
              {savingTime ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Next run:{' '}
            {(c.scheduler_enabled && !c.paused)
              ? <span className="font-mono" data-testid="operator-daily-next">{formatJst(c.next_daily_run_at)}</span>
              : <span className="font-mono text-slate-400" data-testid="operator-daily-next">— {c.paused ? '(paused)' : '(disabled)'}</span>}
            {' · '}scheduler <span className={!c.scheduler_enabled ? 'text-rose-600 font-semibold' : c.paused ? 'text-amber-600 font-semibold' : 'text-emerald-600 font-semibold'}>
              {!c.scheduler_enabled ? 'DISABLED' : c.paused ? 'PAUSED' : 'ACTIVE'}
            </span>
          </p>
          {timeValid && c.scheduler_enabled && !c.paused && (
            <p
              className={`text-[11px] mt-1.5 rounded-md px-2 py-1.5 ${runsToday ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}
              data-testid="operator-daily-time-hint"
            >
              {runsToday
                ? <>This will run <b>today</b> at {chLabel} JST (the time is still ahead today).</>
                : <>{chLabel} JST has <b>already passed today</b>, so the next automatic run is <b>tomorrow</b>. To send a run <b>today</b>, use the <b>“Run now”</b> button above.</>}
            </p>
          )}
          {timeValid && c.paused && (
            <p className="text-[11px] mt-1.5 rounded-md px-2 py-1.5 bg-amber-50 text-amber-700" data-testid="operator-daily-time-hint">
              System is <b>paused</b> — saved, but the daily report won’t send until you resume above.
            </p>
          )}
        </div>

        {/* Weekly time editor */}
        <div className="rounded-lg bg-slate-50 p-3" data-testid="operator-weekly-editor">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> Weekly report day &amp; time (JST)
            <span
              data-testid="operator-weekly-status"
              className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${(c.weekly_scheduler_enabled && !c.paused) ? 'bg-emerald-100 text-emerald-700' : (c.weekly_scheduler_enabled && c.paused) ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'}`}
            >
              {(c.weekly_scheduler_enabled && !c.paused) ? 'AUTOMATION ON' : (c.weekly_scheduler_enabled && c.paused) ? 'PAUSED' : 'AUTOMATION OFF'}
            </span>
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={wDay} onChange={(e) => setWDay(e.target.value)}
              data-testid="operator-weekly-day"
              className="min-h-[44px] px-2 py-1.5 border border-slate-300 rounded-lg text-sm font-mono"
            >
              {DOW.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
            <input
              type="number" min="0" max="23" value={wHour} onChange={(e) => setWHour(e.target.value)}
              data-testid="operator-weekly-hour"
              className="w-16 min-h-[44px] px-2 py-1.5 border border-slate-300 rounded-lg text-sm font-mono text-center"
            />
            <span className="font-bold text-slate-400">:</span>
            <input
              type="number" min="0" max="59" value={wMinute} onChange={(e) => setWMinute(e.target.value)}
              data-testid="operator-weekly-minute"
              className="w-16 min-h-[44px] px-2 py-1.5 border border-slate-300 rounded-lg text-sm font-mono text-center"
            />
            <span className="text-[11px] text-slate-500">JST</span>
            <button
              type="button" onClick={saveWeekly} disabled={savingWeekly}
              data-testid="operator-weekly-time-save"
              className="ml-auto inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 py-1.5 rounded-lg text-sm font-bold text-white bg-slate-900 hover:bg-slate-700 disabled:opacity-50"
            >
              {savingWeekly ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Next weekly run:{' '}
            {(c.weekly_scheduler_enabled && !c.paused)
              ? <span className="font-mono" data-testid="operator-weekly-next">{formatJst(c.next_weekly_run_at)}</span>
              : <span className="font-mono text-slate-400" data-testid="operator-weekly-next">— {c.paused ? '(paused)' : '(automation off — not scheduled)'}</span>}
          </p>
          <p className={`text-[11px] rounded-md px-2 py-1.5 mt-1.5 ${(c.weekly_scheduler_enabled && !c.paused) ? 'text-emerald-700 bg-emerald-50' : 'text-amber-700 bg-amber-50'}`}>
            {c.paused
              ? <>System is <b>paused</b> — no weekly (or any) reports will send until you resume above. Your saved day/time is preserved.</>
              : c.weekly_scheduler_enabled
                ? <>Weekly automation is <b>ON</b>. Auto-sends preserve idempotency — an already-sent week is not re-sent when you change the time.</>
                : <>Weekly automation is <b>OFF</b> (gated). Saving a day/time stores the config but does <b>not</b> register or run any job, so no weekly report will send.</>}
          </p>
        </div>

        {/* Monthly time editor */}
        <div className="rounded-lg bg-slate-50 p-3" data-testid="operator-monthly-editor">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> Monthly report day &amp; time (JST)
            <span
              data-testid="operator-monthly-status"
              className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${(c.monthly_scheduler_enabled && !c.paused) ? 'bg-emerald-100 text-emerald-700' : (c.monthly_scheduler_enabled && c.paused) ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'}`}
            >
              {(c.monthly_scheduler_enabled && !c.paused) ? 'AUTOMATION ON' : (c.monthly_scheduler_enabled && c.paused) ? 'PAUSED' : 'AUTOMATION OFF'}
            </span>
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-slate-500">Day</span>
            <select
              value={mDay} onChange={(e) => setMDay(e.target.value)}
              data-testid="operator-monthly-day"
              className="min-h-[44px] px-2 py-1.5 border border-slate-300 rounded-lg text-sm font-mono"
            >
              {DOM.map((v) => <option key={v} value={String(v)}>{v}</option>)}
            </select>
            <input
              type="number" min="0" max="23" value={mHour} onChange={(e) => setMHour(e.target.value)}
              data-testid="operator-monthly-hour"
              className="w-16 min-h-[44px] px-2 py-1.5 border border-slate-300 rounded-lg text-sm font-mono text-center"
            />
            <span className="font-bold text-slate-400">:</span>
            <input
              type="number" min="0" max="59" value={mMinute} onChange={(e) => setMMinute(e.target.value)}
              data-testid="operator-monthly-minute"
              className="w-16 min-h-[44px] px-2 py-1.5 border border-slate-300 rounded-lg text-sm font-mono text-center"
            />
            <span className="text-[11px] text-slate-500">JST</span>
            <button
              type="button" onClick={saveMonthly} disabled={savingMonthly}
              data-testid="operator-monthly-time-save"
              className="ml-auto inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 py-1.5 rounded-lg text-sm font-bold text-white bg-slate-900 hover:bg-slate-700 disabled:opacity-50"
            >
              {savingMonthly ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Next monthly run:{' '}
            {(c.monthly_scheduler_enabled && !c.paused)
              ? <span className="font-mono" data-testid="operator-monthly-next">{formatJst(c.next_monthly_run_at)}</span>
              : <span className="font-mono text-slate-400" data-testid="operator-monthly-next">— {c.paused ? '(paused)' : '(automation off — not scheduled)'}</span>}
          </p>
          <p className={`text-[11px] rounded-md px-2 py-1.5 mt-1.5 ${(c.monthly_scheduler_enabled && !c.paused) ? 'text-emerald-700 bg-emerald-50' : 'text-amber-700 bg-amber-50'}`}>
            {c.paused
              ? <>System is <b>paused</b> — no monthly (or any) reports will send until you resume above. Your saved day/time is preserved.</>
              : c.monthly_scheduler_enabled
                ? <>Monthly automation is <b>ON</b>. Auto-sends preserve idempotency — an already-sent month is not re-sent when you change the time. (Day is capped at 28 so a run never skips a short month.)</>
                : <>Monthly automation is <b>OFF</b> (gated). Saving a day/time stores the config but does <b>not</b> register or run any job, so no monthly report will send.</>}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

/** Slim global banner shown across admin when the system is paused. */
export const PausedBanner = ({ controls }) => {
  if (!controls?.paused) return null;
  return (
    <div className="bg-rose-600 text-white text-center text-xs font-bold py-1.5 px-4 flex items-center justify-center gap-2" data-testid="global-paused-banner">
      <PauseCircle className="w-4 h-4" />
      SYSTEM PAUSED — MEISAI processing &amp; report sending are halted{controls.pause_reason ? ` (${controls.pause_reason})` : ''}. Resume under Scheduler Health → Operator Controls.
    </div>
  );
};

/** Self-fetching global banner — drop anywhere in the admin shell. Renders
 * nothing unless paused (and silently no-ops for non-host users). Polls. */
export const GlobalPausedBanner = () => {
  const { user } = useAuth();
  const isHost = user?.role === 'host';
  const [controls, setControls] = useState(null);
  useEffect(() => {
    // runtime-controls is a host-only endpoint. Skip polling for non-host
    // admins so we don't fire guaranteed-403 requests on every admin page.
    if (!isHost) return undefined;
    let stop = false;
    const poll = async () => {
      try {
        const res = await authedFetch(`${CTRL}`, { credentials: 'include' });
        if (res.ok) { const d = await res.json(); if (!stop) setControls(d); }
      } catch { /* noop */ }
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { stop = true; clearInterval(id); };
  }, [isHost]);
  return <PausedBanner controls={controls} />;
};
